import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Inbox, Mail, Phone, Search, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { ApproveInquiryDialog } from './ApproveInquiryDialog';
import { PackageFunnelAnalytics } from './PackageFunnelAnalytics';

type Status = 'new' | 'contacted' | 'qualified' | 'won' | 'lost';

interface Inquiry {
  id: string;
  package_id: string | null;
  full_name: string;
  company: string | null;
  phone: string;
  email: string;
  audience: string | null;
  message: string | null;
  status: Status;
  created_at: string;
}

interface PackageRef { id: string; name: string; tier_label: string | null; }

const STATUS_VARIANT: Record<Status, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  new: 'default', contacted: 'secondary', qualified: 'secondary', won: 'outline', lost: 'destructive',
};

export function PackageInquiriesPanel() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [approveOpen, setApproveOpen] = useState(false);
  const [activeInquiry, setActiveInquiry] = useState<Inquiry | null>(null);

  const { data: packages } = useQuery({
    queryKey: ['inq-packages-ref'],
    queryFn: async () => {
      const { data } = await supabase.from('advertising_packages' as any).select('id,name,tier_label');
      return (data as unknown as PackageRef[]) || [];
    },
  });
  const pkgMap = new Map((packages || []).map((p) => [p.id, p]));

  const { data: inquiries, isLoading } = useQuery({
    queryKey: ['package-inquiries', status],
    queryFn: async () => {
      let q = supabase.from('package_inquiries' as any).select('*').order('created_at', { ascending: false });
      if (status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data as unknown as Inquiry[]) || [];
    },
  });

  const filtered = (inquiries || []).filter((i) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return [i.full_name, i.email, i.phone, i.company, i.message].filter(Boolean).some((v) => v!.toLowerCase().includes(s));
  });

  const setInquiryStatus = async (id: string, next: Status) => {
    const { error } = await supabase.from('package_inquiries' as any).update({ status: next }).eq('id', id);
    if (error) return toast.error(error.message);
    toast.success(`Marked as ${next}`);
    qc.invalidateQueries({ queryKey: ['package-inquiries'] });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Inbox className="h-5 w-5 text-primary" /> Package Inquiries</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search name, email, phone…" className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="won">Won</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? <Skeleton className="h-48 w-full" /> : filtered.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">No inquiries yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Lead</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((i) => {
                const pkg = i.package_id ? pkgMap.get(i.package_id) : null;
                return (
                  <TableRow key={i.id}>
                    <TableCell className="text-xs whitespace-nowrap">{format(new Date(i.created_at), 'd MMM, HH:mm')}</TableCell>
                    <TableCell>
                      <div className="font-medium">{i.full_name}</div>
                      {i.company && <div className="text-xs text-muted-foreground">{i.company}</div>}
                      {i.audience && <Badge variant="outline" className="text-[10px] mt-1 capitalize">{i.audience}</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {pkg ? <>{pkg.tier_label || pkg.name}</> : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs space-y-0.5">
                      <a href={`mailto:${i.email}`} className="flex items-center gap-1 text-primary hover:underline"><Mail className="h-3 w-3" />{i.email}</a>
                      <a href={`tel:${i.phone}`} className="flex items-center gap-1 text-primary hover:underline"><Phone className="h-3 w-3" />{i.phone}</a>
                    </TableCell>
                    <TableCell className="text-xs max-w-xs truncate" title={i.message || ''}>{i.message || '—'}</TableCell>
                    <TableCell>
                      <Select value={i.status} onValueChange={(v) => setInquiryStatus(i.id, v as Status)}>
                        <SelectTrigger className="h-8 w-32 text-xs">
                          <Badge variant={STATUS_VARIANT[i.status]} className="capitalize">{i.status}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="won">Won</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant={i.status === 'won' ? 'outline' : 'default'}
                        disabled={i.status === 'won'}
                        onClick={() => { setActiveInquiry(i); setApproveOpen(true); }}
                      >
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {i.status === 'won' ? 'Approved' : 'Approve'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <ApproveInquiryDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        inquiry={activeInquiry}
      />

      <div className="px-6 pb-6">
        <PackageFunnelAnalytics />
      </div>
    </Card>
  );
}
